/* eslint-disable no-promise-executor-return */
/* eslint-disable no-await-in-loop */

import { AppContext } from '@context';
import { EstimateDocument } from '../../components/documents';
import { getDesignImage, getEstimatedTax, toImageDataUrl } from '../../helpers';
import { pdf } from '@react-pdf/renderer';
import { Kitchen3dController as TdComponentController } from '@rta/rta-blueprint-3d-component';
import { useContext } from 'react';
import {
	ContentPanelMode,
	customerState,
	projectState,
	selectNavigation,
	setContentPanelMode,
	useAppDispatch,
	useAppSelector,
} from '@state';
export const useGenerateEstimateBlob = () => {
	const {
		appState: { customerDesigns: designs },
	} = useContext(AppContext);
	let controller: TdComponentController | null | undefined = null;

	const appNav = useAppSelector(selectNavigation);
	const customer = useAppSelector(customerState);
	const project = useAppSelector(projectState);
	const dispatch = useAppDispatch();

	const generate = async (): Promise<any> => {
		const design =
			designs.designs.find((bp) => bp.blueprint.id === appNav.designId) ??
			null;
		controller = design?.tdController;

		if (!controller) {
			throw new Error(
				'Could not generate estimate: 3D controller not available.'
			);
		}

		// wait for Image - same pattern as download feature
		const waitForImage = async () => {
			const isRendering = () => {
				const img = controller?.getImage();
				const rendering = controller?.getRendering();
				return !img || img === '' || rendering === true;
			};
			while (isRendering()) {
				await new Promise((resolve) => setTimeout(resolve, 1000));
			}

			// Wait for textures to fully load by checking if image stabilizes
			let previousImage = controller?.getImage() ?? '';
			let stableCount = 0;
			const requiredStableChecks = 3; // Image must be unchanged for 3 consecutive checks
			const maxWaitTime = 15000; // Maximum 15 seconds for texture loading
			const checkInterval = 500; // Check every 500ms
			const startTime = Date.now();

			while (stableCount < requiredStableChecks && (Date.now() - startTime) < maxWaitTime) {
				await new Promise((resolve) => setTimeout(resolve, checkInterval));

				// Force a re-render to capture any newly loaded textures
				controller?.render();
				await new Promise((resolve) => setTimeout(resolve, 100)); // Small delay for render to complete

				const currentImage = controller?.getImage() ?? '';

				if (currentImage === previousImage && currentImage !== '') {
					stableCount++;
				} else {
					stableCount = 0;
				}
				previousImage = currentImage;
			}

			const image = controller?.getImage();
			return image ?? '';
		};

		// Prefer the design's saved rendering — no live 3D render/screenshot needed
		const savedRendering = getDesignImage(design?.blueprint);
		let imageData: string;
		if (savedRendering) {
			imageData = await toImageDataUrl(savedRendering);
		} else {
			dispatch(setContentPanelMode(ContentPanelMode.ThreeD));
			imageData = await waitForImage();
		}

		const kitchenData = controller?.kitchen ?? null;
		if (!customer || !kitchenData || !project) {
			console.error('Missing data for EstimateDocument:', {
				hasCustomer: !!customer,
				hasKitchen: !!kitchenData,
				hasProject: !!project,
			});
			throw new Error('Missing required data for estimate generation');
		}

		const { tax: currentTax } = getEstimatedTax({ kitchen: kitchenData, customer, project });

		const EstimateDoc = (
			<EstimateDocument
				data={{
					customer,
					project,
					tax: currentTax,
					kitchen: kitchenData,
					image: imageData,
				}}
			/>
		);

		const blob = await pdf(EstimateDoc).toBlob();

		return blob;
	};
	return generate;
};
